import "@nimbus-ds/styles/dist/index.css";
import "@nimbus-ds/styles/dist/themes/dark.css";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Icon,
  IconButton,
  Input,
  SegmentedControl,
  Text,
  Textarea,
  Title,
} from "@nimbus-ds/components";
import { Menu } from "@nimbus-ds/patterns";
import { ThemeProvider } from "@nimbus-ds/styles";
import {
  CalendarDaysIcon,
  ChevronRightIcon,
  CogIcon,
  DisketteIcon,
  FileIcon,
  GlobeIcon,
  GuitarIcon,
  HomeIcon,
  MoonIcon,
  PencilIcon,
  PictureIcon,
  PlusIcon,
  RepeatIcon,
  SearchIcon,
  ExternalLinkIcon,
  SunIcon,
  TrashIcon,
} from "@nimbus-ds/icons";
import { useEffect, useMemo, useState } from "react";
import {
  clearConnection,
  DEFAULT_API_BASE,
  deleteContentItem,
  fetchInstantorsContent,
  getStoredConnection,
  saveConnection,
  saveContentItem,
  saveSiteContent,
} from "./api";
import { ADMIN_LOCALES, CONTENT_GROUPS } from "./config";

const ADMIN_THEME_KEY = "instantors_admin_theme";

const navItems = [
  { id: "dashboard", label: "Dashboard", icon: HomeIcon },
  { id: "content", label: "Website Content", icon: GlobeIcon },
  { id: "shows", label: "Shows", icon: CalendarDaysIcon },
  { id: "albums", label: "Albums", icon: DisketteIcon },
  { id: "media", label: "Media", icon: PictureIcon },
  { id: "posts", label: "News Posts", icon: FileIcon },
  { id: "settings", label: "Settings", icon: CogIcon },
];

const CONTENT_TYPES = {
  shows: {
    title: "Shows",
    singular: "Show",
    listKey: "shows",
    empty: "No shows published yet.",
    fields: [
      ["date", "Show date", "date"],
      ["value", "Ticket value", "text"],
      ["url", "Ticket URL", "url"],
      ["image", "Image URL", "url"],
    ],
  },
  albums: {
    title: "Albums",
    singular: "Album",
    listKey: "albums",
    empty: "No albums published yet.",
    fields: [
      ["year", "Release year", "text"],
      ["url", "Listen URL", "url"],
      ["image", "Cover image URL", "url"],
    ],
  },
  media: {
    title: "Media",
    singular: "Media item",
    listKey: "media",
    empty: "No media items published yet.",
    fields: [
      ["type", "Media type", "text"],
      ["url", "External URL", "url"],
      ["image", "Thumbnail URL", "url"],
    ],
  },
  posts: {
    title: "News Posts",
    singular: "News post",
    listKey: "posts",
    empty: "No posts published yet.",
    fields: [
      ["url", "External/permalink URL", "url"],
      ["image", "Image URL", "url"],
    ],
  },
};

function createEmptySiteContent() {
  return ADMIN_LOCALES.reduce((locales, locale) => {
    locales[locale.code] = {};
    CONTENT_GROUPS.forEach((group) => {
      group.fields.forEach(([field]) => {
        locales[locale.code][field] = "";
      });
    });
    return locales;
  }, {});
}

function mergeSiteContent(payloads) {
  const next = createEmptySiteContent();

  payloads.forEach((payload) => {
    if (payload?.locale) {
      next[payload.locale] = {
        ...next[payload.locale],
        ...(payload.site || {}),
      };
    }
  });

  return next;
}

function createEmptyLocalizedItem() {
  return ADMIN_LOCALES.reduce((localized, locale) => {
    localized[locale.code] = {
      title: "",
      subtitle: "",
      description: "",
    };
    return localized;
  }, {});
}

function createEmptyItem(type) {
  const extra = CONTENT_TYPES[type].fields.reduce((fields, [field]) => {
    fields[field] = "";
    return fields;
  }, {});

  return {
    id: null,
    localized: createEmptyLocalizedItem(),
    extra,
  };
}

function itemToDraft(type, item) {
  const draft = createEmptyItem(type);

  return {
    ...draft,
    id: item.id || null,
    localized: {
      ...draft.localized,
      ...(item.localized || {
        en: {
          title: item.title || "",
          subtitle: item.subtitle || "",
          description: item.description || "",
        },
      }),
    },
    extra: {
      ...draft.extra,
      ...(item.extra || {}),
      image: item.extra?.image || item.image || "",
    },
  };
}

function getInitialTheme() {
  try {
    return window.localStorage.getItem(ADMIN_THEME_KEY) === "dark" ? "dark" : "base";
  } catch (error) {
    return "base";
  }
}

export function AdminApp() {
  const [theme, setTheme] = useState(getInitialTheme);
  const [connection, setConnection] = useState(() => getStoredConnection());
  const [connectionDraft, setConnectionDraft] = useState(() => ({
    apiBase: getStoredConnection()?.apiBase || DEFAULT_API_BASE,
    username: getStoredConnection()?.username || "",
    applicationPassword: getStoredConnection()?.applicationPassword || "",
  }));
  const [activeView, setActiveView] = useState("dashboard");
  const [activeLocale, setActiveLocale] = useState("en");
  const [content, setContent] = useState(null);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [itemDrafts, setItemDrafts] = useState(() =>
    Object.keys(CONTENT_TYPES).reduce((drafts, type) => {
      drafts[type] = createEmptyItem(type);
      return drafts;
    }, {})
  );
  const [itemLocales, setItemLocales] = useState(() =>
    Object.keys(CONTENT_TYPES).reduce((locales, type) => {
      locales[type] = "en";
      return locales;
    }, {})
  );

  const isDark = theme === "dark";
  const activeLabel = navItems.find((item) => item.id === activeView)?.label || "Dashboard";

  async function loadContent(nextConnection = connection) {
    setStatus("loading");
    setMessage("");

    try {
      const payloads = await Promise.all(
        ADMIN_LOCALES.map((locale) => fetchInstantorsContent(nextConnection, locale.code))
      );
      setContent({
        site: mergeSiteContent(payloads),
        lists: payloads[0],
      });
      setStatus("ready");
    } catch (error) {
      setStatus("error");
      setMessage(error.message);
    }
  }

  useEffect(() => {
    loadContent(connection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(ADMIN_THEME_KEY, theme);
    } catch (error) {
      // Ignore storage failures; the toggle still works for the current session.
    }
  }, [theme]);

  function handleConnect(event) {
    event.preventDefault();
    const nextConnection = {
      apiBase: connectionDraft.apiBase || DEFAULT_API_BASE,
      username: connectionDraft.username,
      applicationPassword: connectionDraft.applicationPassword,
    };
    saveConnection(nextConnection);
    setConnection(nextConnection);
    loadContent(nextConnection);
  }

  function handleDisconnect() {
    clearConnection();
    setConnection(null);
    setConnectionDraft({
      apiBase: DEFAULT_API_BASE,
      username: "",
      applicationPassword: "",
    });
  }

  function updateField(locale, field, value) {
    setContent((current) => ({
      ...current,
      site: {
        ...current.site,
        [locale]: {
          ...current.site[locale],
          [field]: value,
        },
      },
    }));
  }

  async function handleSaveContent() {
    if (!connection?.username || !connection?.applicationPassword) {
      setMessage("Connect with WordPress before saving.");
      setActiveView("settings");
      return;
    }

    setIsSaving(true);
    setMessage("");

    try {
      const result = await saveSiteContent(connection, content.site);
      setContent((current) => ({
        ...current,
        site: result.site || current.site,
      }));
      setMessage("Website content saved in WordPress.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  }

  function requireConnection() {
    if (connection?.username && connection?.applicationPassword) {
      return true;
    }

    setMessage("Connect with WordPress before changing content.");
    setActiveView("settings");
    return false;
  }

  function updateItemLocalized(type, locale, field, value) {
    setItemDrafts((current) => ({
      ...current,
      [type]: {
        ...current[type],
        localized: {
          ...current[type].localized,
          [locale]: {
            ...current[type].localized[locale],
            [field]: value,
          },
        },
      },
    }));
  }

  function updateItemExtra(type, field, value) {
    setItemDrafts((current) => ({
      ...current,
      [type]: {
        ...current[type],
        extra: {
          ...current[type].extra,
          [field]: value,
        },
      },
    }));
  }

  function editItem(type, item) {
    setItemDrafts((current) => ({
      ...current,
      [type]: itemToDraft(type, item),
    }));
  }

  function resetItem(type) {
    setItemDrafts((current) => ({
      ...current,
      [type]: createEmptyItem(type),
    }));
  }

  async function handleSaveItem(type) {
    if (!requireConnection()) {
      return;
    }

    setIsSaving(true);
    setMessage("");

    try {
      await saveContentItem(connection, type, itemDrafts[type]);
      resetItem(type);
      await loadContent(connection);
      setMessage(`${CONTENT_TYPES[type].singular} saved in WordPress.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteItem(type, id) {
    if (!requireConnection()) {
      return;
    }

    if (!window.confirm(`Move this ${CONTENT_TYPES[type].singular.toLowerCase()} to trash?`)) {
      return;
    }

    setIsSaving(true);
    setMessage("");

    try {
      await deleteContentItem(connection, type, id);
      await loadContent(connection);
      setMessage(`${CONTENT_TYPES[type].singular} moved to trash.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  }

  const stats = useMemo(() => {
    const lists = content?.lists || {};
    return [
      { label: "Languages", value: ADMIN_LOCALES.length, icon: GlobeIcon },
      { label: "Shows", value: lists.shows?.length || 0, icon: CalendarDaysIcon },
      { label: "Albums", value: lists.albums?.length || 0, icon: DisketteIcon },
      { label: "News Posts", value: lists.posts?.length || 0, icon: FileIcon },
    ];
  }, [content]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        backgroundColor="neutral-surface"
        color="neutral-textHigh"
        data-theme={theme}
        display="grid"
        gridTemplateColumns={{ xs: "1fr", lg: "280px minmax(0, 1fr)" }}
        minHeight="100vh"
      >
        <Box
          as="aside"
          backgroundColor="neutral-background"
          borderColor="neutral-surfaceHighlight"
          borderRightWidth={{ xs: "none", lg: "1" }}
          borderStyle="solid"
          display="flex"
          flexDirection="column"
          gap="6"
          minHeight={{ xs: "auto", lg: "100vh" }}
          padding="6"
          position={{ xs: "relative", lg: "sticky" }}
          top="0"
        >
          <Menu expanded>
            <Menu.Header>
              <Box display="flex" gap="2" alignItems="center" width="100%">
                <Icon color="neutral-textHigh" source={<GuitarIcon size="medium" />} />
                <Box display="inline-flex" flex="1">
                  <Text fontSize="base" color="neutral-textHigh" fontWeight="bold">
                    The Instantors
                  </Text>
                </Box>
                <IconButton
                  aria-label="Open public website"
                  onClick={() => window.open("/", "_blank", "noopener,noreferrer")}
                  source={<ExternalLinkIcon />}
                  size="2rem"
                />
              </Box>
            </Menu.Header>

            <Menu.Body>
              <Menu.Section>
                <Menu.Button
                  active={activeView === "dashboard"}
                  label="Dashboard"
                  onClick={() => setActiveView("dashboard")}
                  startIcon={HomeIcon}
                />
              </Menu.Section>

              <Menu.Section title="Content">
                <Menu.ButtonAccordion
                  active={["content", "shows", "albums", "media", "posts"].includes(activeView)}
                  contentid="instantors-content-menu"
                  menuButton={{
                    id: "instantors-content-control",
                    startIcon: GlobeIcon,
                    label: "Website",
                    children: <Badge appearance="primary" count={String(ADMIN_LOCALES.length)} />,
                    "aria-controls": "instantors-content-menu",
                  }}
                  open
                >
                  <Menu.Button
                    active={activeView === "content"}
                    label="Website Content"
                    onClick={() => setActiveView("content")}
                  />
                  <Menu.Button
                    active={activeView === "shows"}
                    label="Shows"
                    onClick={() => setActiveView("shows")}
                  />
                  <Menu.Button
                    active={activeView === "albums"}
                    label="Albums"
                    onClick={() => setActiveView("albums")}
                  />
                  <Menu.Button
                    active={activeView === "media"}
                    label="Media"
                    onClick={() => setActiveView("media")}
                  />
                  <Menu.Button
                    active={activeView === "posts"}
                    label="News Posts"
                    onClick={() => setActiveView("posts")}
                  >
                    <Badge appearance="neutral" count={String(content?.lists?.posts?.length || 0)} />
                  </Menu.Button>
                </Menu.ButtonAccordion>
              </Menu.Section>

              <Menu.Section title="Create">
                <Menu.Button
                  label="New content"
                  onClick={() => setActiveView("content")}
                  startIcon={PlusIcon}
                />
              </Menu.Section>
            </Menu.Body>

            <Menu.Footer
              active={activeView === "settings"}
              label="Settings"
              onClick={() => setActiveView("settings")}
              startIcon={CogIcon}
            />
          </Menu>
        </Box>

        <Box as="main" minWidth="0">
          <Box
            as="header"
            alignItems="center"
            backgroundColor="neutral-background"
            borderBottomWidth="1"
            borderColor="neutral-surfaceHighlight"
            borderStyle="solid"
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            gap="4"
            justifyContent="space-between"
            paddingX={{ xs: "4", md: "8" }}
            paddingY="4"
            position="sticky"
            top="0"
            zIndex="100"
          >
            <Box
              alignItems="center"
              backgroundColor="neutral-surface"
              borderColor="neutral-surfaceHighlight"
              borderRadius="3"
              borderStyle="solid"
              borderWidth="1"
              color="neutral-textLow"
              display="flex"
              gap="2"
              minHeight="40px"
              paddingX="3"
              width={{ xs: "100%", md: "420px" }}
            >
              <Icon color="neutral-textLow" source={<SearchIcon size="small" />} />
              <Text as="span" color="neutral-textLow">
                Search content
              </Text>
            </Box>

            <Box alignItems="center" display="flex" flexWrap="wrap" gap="3">
              <Button appearance="transparent" onClick={() => loadContent(connection)}>
                <Icon source={<RepeatIcon size="small" />} />
                Refresh
              </Button>

              <Button
                appearance={isDark ? "primary" : "neutral"}
                aria-pressed={isDark}
                onClick={() => setTheme(isDark ? "base" : "dark")}
                type="button"
              >
                <Icon
                  color={isDark ? "neutral-background" : "neutral-textHigh"}
                  source={isDark ? <MoonIcon size="small" /> : <SunIcon size="small" />}
                />
                Dark theme
              </Button>
            </Box>
          </Box>

          <Box
            as="section"
            alignItems={{ xs: "flex-start", md: "center" }}
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            gap="4"
            justifyContent="space-between"
            paddingBottom="4"
            paddingTop="8"
            paddingX={{ xs: "4", md: "8" }}
          >
            <Box>
              <Text color="neutral-textLow" fontSize="caption">
                {activeView === "dashboard" ? "Overview" : "Content studio"}
              </Text>
              <Title as="h1">{activeLabel}</Title>
            </Box>
            <Badge appearance={connection?.username ? "success" : "neutral"}>
              {connection?.username ? "Connected" : "Local preview"}
            </Badge>
          </Box>

          <Box
            as="section"
            display="grid"
            gap="5"
            paddingBottom="8"
            paddingX={{ xs: "4", md: "8" }}
          >
            {message && (
              <Alert appearance="primary" title="Admin notice">
                {message}
              </Alert>
            )}

            {status === "loading" && <AdminCard>Loading WordPress content...</AdminCard>}
            {status === "error" && (
              <Alert appearance="danger" title="Unable to load content">
                Check the WordPress API URL in Settings.
              </Alert>
            )}

            {status !== "loading" && status !== "error" && (
              <>
                {activeView === "dashboard" && (
                  <Dashboard stats={stats} content={content} onNavigate={setActiveView} />
                )}
                {activeView === "content" && (
                  <WebsiteContent
                    activeLocale={activeLocale}
                    content={content}
                    isSaving={isSaving}
                    onLocaleChange={setActiveLocale}
                    onSave={handleSaveContent}
                    onUpdate={updateField}
                  />
                )}
                {["shows", "albums", "media", "posts"].includes(activeView) && (
                  <ContentManager
                    activeLocale={itemLocales[activeView]}
                    content={content}
                    draft={itemDrafts[activeView]}
                    isSaving={isSaving}
                    onDelete={handleDeleteItem}
                    onEdit={editItem}
                    onExtraChange={updateItemExtra}
                    onLocaleChange={(locale) =>
                      setItemLocales((current) => ({ ...current, [activeView]: locale }))
                    }
                    onLocalizedChange={updateItemLocalized}
                    onReset={resetItem}
                    onSave={handleSaveItem}
                    type={activeView}
                  />
                )}
                {activeView === "settings" && (
                  <SettingsPanel
                    connection={connection}
                    draft={connectionDraft}
                    onChange={setConnectionDraft}
                    onConnect={handleConnect}
                    onDisconnect={handleDisconnect}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

function AdminCard({ children }) {
  return <Card padding="base">{children}</Card>;
}

function AdminSectionHeader({ title, description, action }) {
  return (
    <Box
      alignItems="flex-start"
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      gap="4"
      justifyContent="space-between"
      marginBottom="5"
    >
      <Box>
        <Title as="h2">{title}</Title>
        {description && <Text color="neutral-textLow">{description}</Text>}
      </Box>
      {action}
    </Box>
  );
}

function AdminTabs({ active, onChange }) {
  return (
    <Box marginBottom="5">
      <SegmentedControl
        fullWidth
        onSegmentsSelect={(selected) => selected[0] && onChange(selected[0])}
        selectedSegments={[active]}
      >
      {ADMIN_LOCALES.map((locale) => (
        <SegmentedControl.Button
          fullWidth
          id={locale.code}
          key={locale.code}
          label={`${locale.label} ${locale.code}`}
        >
          {locale.label}
        </SegmentedControl.Button>
      ))}
      </SegmentedControl>
    </Box>
  );
}

function AdminField({ label, children, wide = false }) {
  return (
    <Box as="label" display="grid" gap="2" gridColumn={wide ? "1 / -1" : "auto"}>
      <Text as="span" color="neutral-textLow" fontSize="caption" fontWeight="medium">
        {label}
      </Text>
      {children}
    </Box>
  );
}

function Dashboard({ stats, content, onNavigate }) {
  return (
    <Box display="grid" gap="5">
      <Box
        display="grid"
        gap="4"
        gridTemplateColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" }}
      >
        {stats.map((stat) => {
          const StatIcon = stat.icon;
          return (
            <AdminCard key={stat.label}>
              <Box alignItems="center" display="flex" gap="4">
                <Icon source={<StatIcon size="medium" />} />
                <Box display="grid" gap="1">
                  <Title as="h4">{stat.value}</Title>
                  <Text color="neutral-textLow" fontSize="caption">
                    {stat.label}
                  </Text>
                </Box>
              </Box>
            </AdminCard>
          );
        })}
      </Box>

      <AdminCard>
        <AdminSectionHeader
          title="Admin sections"
          description="Start here. We will refine each screen one by one using the Nimbus system."
        />
        <Box
          display="grid"
          gap="3"
          gridTemplateColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
        >
          {Object.entries(CONTENT_TYPES).map(([type, config]) => (
            <Button appearance="neutral" fullWidth key={type} onClick={() => onNavigate(type)}>
              <Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
                <Box display="grid" gap="1">
                  <Text as="span" fontWeight="bold">
                    {config.title}
                  </Text>
                  <Text as="span" color="neutral-textLow" fontSize="caption">
                    {content?.lists?.[config.listKey]?.length || 0} published items
                  </Text>
                </Box>
              <Icon source={<ChevronRightIcon size="small" />} />
              </Box>
            </Button>
          ))}
          <Button appearance="neutral" fullWidth onClick={() => onNavigate("content")}>
            <Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
              <Box display="grid" gap="1">
                <Text as="span" fontWeight="bold">
                  Website Content
                </Text>
                <Text as="span" color="neutral-textLow" fontSize="caption">
                  {ADMIN_LOCALES.length} languages prepared
                </Text>
              </Box>
              <Icon source={<ChevronRightIcon size="small" />} />
            </Box>
          </Button>
        </Box>
      </AdminCard>
    </Box>
  );
}

function WebsiteContent({ activeLocale, content, isSaving, onLocaleChange, onSave, onUpdate }) {
  const site = content?.site || createEmptySiteContent();

  return (
    <AdminCard>
      <AdminSectionHeader
        title="Website Content"
        description="Global copy for the institutional website. English remains the default language."
        action={
          <Button appearance="primary" disabled={isSaving} onClick={onSave}>
            <Icon color="neutral-background" source={<DisketteIcon size="small" />} />
            {isSaving ? "Saving..." : "Save content"}
          </Button>
        }
      />

      <AdminTabs active={activeLocale} onChange={onLocaleChange} />

      <Box display="grid" gap="5">
        {CONTENT_GROUPS.map((group) => (
          <Card backgroundColor="neutral-surface" key={group.key} padding="base">
            <Title as="h3">{group.title}</Title>
            <Text color="neutral-textLow" fontSize="caption">
              {group.description}
            </Text>
            <Box
              display="grid"
              gap="4"
              gridTemplateColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
              marginTop="4"
            >
              {group.fields.map(([field, label, type]) => (
                <AdminField key={field} label={label} wide={type === "textarea"}>
                  {type === "textarea" ? (
                    <Textarea
                      id={`site-${activeLocale}-${field}`}
                      lines={field === "about_paragraphs" ? 6 : 4}
                      value={site[activeLocale]?.[field] || ""}
                      onChange={(event) => onUpdate(activeLocale, field, event.target.value)}
                    />
                  ) : (
                    <Input
                      value={site[activeLocale]?.[field] || ""}
                      onChange={(event) => onUpdate(activeLocale, field, event.target.value)}
                    />
                  )}
                </AdminField>
              ))}
            </Box>
          </Card>
        ))}
      </Box>
    </AdminCard>
  );
}

function ContentManager({
  activeLocale,
  content,
  draft,
  isSaving,
  onDelete,
  onEdit,
  onExtraChange,
  onLocaleChange,
  onLocalizedChange,
  onReset,
  onSave,
  type,
}) {
  const config = CONTENT_TYPES[type];
  const rows = content?.lists?.[config.listKey] || [];

  return (
    <Box display="grid" gap="5">
      <AdminCard>
        <AdminSectionHeader
          title={draft.id ? `Edit ${config.singular}` : `New ${config.singular}`}
          description="This functional shell will be refined when we design this specific screen."
          action={
            draft.id ? (
              <Button appearance="transparent" onClick={() => onReset(type)}>
                Cancel edit
              </Button>
            ) : null
          }
        />

        <AdminTabs active={activeLocale} onChange={onLocaleChange} />

        <Box
          display="grid"
          gap="4"
          gridTemplateColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
        >
          <AdminField label="Title" wide>
            <Input
              value={draft.localized[activeLocale]?.title || ""}
              onChange={(event) => onLocalizedChange(type, activeLocale, "title", event.target.value)}
            />
          </AdminField>
          <AdminField label="Subtitle">
            <Input
              value={draft.localized[activeLocale]?.subtitle || ""}
              onChange={(event) =>
                onLocalizedChange(type, activeLocale, "subtitle", event.target.value)
              }
            />
          </AdminField>
          <AdminField label="Description" wide>
            <Textarea
              id={`${type}-${activeLocale}-description`}
              lines={4}
              value={draft.localized[activeLocale]?.description || ""}
              onChange={(event) =>
                onLocalizedChange(type, activeLocale, "description", event.target.value)
              }
            />
          </AdminField>
          {config.fields.map(([field, label, inputType]) => (
            <AdminField key={field} label={label}>
              <Input
                type={inputType}
                value={draft.extra[field] || ""}
                onChange={(event) => onExtraChange(type, field, event.target.value)}
              />
            </AdminField>
          ))}
        </Box>

        <Box alignItems="center" display="flex" gap="3" marginTop="5">
          <Button appearance="primary" disabled={isSaving} onClick={() => onSave(type)}>
            <Icon color="neutral-background" source={<DisketteIcon size="small" />} />
            {isSaving ? "Saving..." : `Save ${config.singular}`}
          </Button>
        </Box>
      </AdminCard>

      <AdminCard>
        <AdminSectionHeader
          title={config.title}
          description="Published items stored in WordPress."
          action={<Badge appearance="neutral">{rows.length} items</Badge>}
        />
        <Box display="grid" gap="3">
          {rows.length === 0 && <Text color="neutral-textLow">{config.empty}</Text>}
          {rows.map((item) => (
            <Card backgroundColor="neutral-surface" key={item.id} padding="base">
              <Box
                alignItems={{ xs: "flex-start", md: "center" }}
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                gap="4"
                justifyContent="space-between"
              >
                <Box display="grid" gap="1">
                  <Text as="span" fontWeight="bold">
                    {item.title}
                  </Text>
                  <Text as="span" color="neutral-textLow" fontSize="caption">
                    {item.description || item.subtitle || "No description"}
                  </Text>
                </Box>
                <Box alignItems="center" display="flex" flexWrap="wrap" gap="3">
                <Button appearance="transparent" size="small" onClick={() => onEdit(type, item)}>
                  <Icon source={<PencilIcon size="small" />} />
                  Edit
                </Button>
                <Button appearance="danger" size="small" onClick={() => onDelete(type, item.id)}>
                  <Icon color="neutral-background" source={<TrashIcon size="small" />} />
                  Delete
                </Button>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      </AdminCard>
    </Box>
  );
}

function SettingsPanel({ connection, draft, onChange, onConnect, onDisconnect }) {
  return (
    <AdminCard>
      <AdminSectionHeader
        title="WordPress Connection"
        description="Use a WordPress user and Application Password to save protected content."
        action={
          connection?.username ? <Badge appearance="success">Connected</Badge> : <Badge>Not connected</Badge>
        }
      />

      <Box
        as="form"
        display="grid"
        gap="4"
        gridTemplateColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
        onSubmit={onConnect}
      >
        <AdminField label="WordPress REST API base" wide>
          <Input
            value={draft.apiBase}
            onChange={(event) => onChange({ ...draft, apiBase: event.target.value })}
            placeholder="https://wordpress.example.com/wp-json"
          />
        </AdminField>
        <AdminField label="Username">
          <Input
            value={draft.username}
            onChange={(event) => onChange({ ...draft, username: event.target.value })}
            placeholder="admin"
          />
        </AdminField>
        <AdminField label="Application Password">
          <Input
            type="password"
            value={draft.applicationPassword}
            onChange={(event) =>
              onChange({ ...draft, applicationPassword: event.target.value })
            }
            placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
          />
        </AdminField>
        <Box alignItems="center" display="flex" flexWrap="wrap" gap="3" gridColumn="1 / -1">
          <Button appearance="primary" type="submit">
            Save connection
          </Button>
          <Button appearance="transparent" type="button" onClick={onDisconnect}>
            Disconnect
          </Button>
        </Box>
      </Box>
    </AdminCard>
  );
}
